/**
 * Identity Engine 类型定义
 * v9.0 新增：AI 数字分身管理功能
 * 
 * 参考文档：docs/shared/api-protocol/identity-engine-api.md
 */

// ==================== 枚举类型 ====================

/**
 * 身份状态
 */
export type IdentityStatus = 'draft' | 'learning' | 'active' | 'archived' | 'failed'

/**
 * 声音状态
 * v10.1 更新：新增 pending 和 published 状态
 */
export type VoiceStatus = 'none' | 'pending' | 'training' | 'ready' | 'published' | 'failed'

/**
 * 语料类型
 */
export type CorpusType = 'weibo' | 'interview' | 'live_chat' | 'dialog' | 'other'

/**
 * 学习阶段
 */
export type LearningStage = 'preparing' | 'style' | 'persona' | 'emotion' | 'boundary' | 'finalizing'

/**
 * 音频样本状态
 */
export type VoiceSampleStatus = 'processing' | 'passed' | 'failed'

/**
 * TTS 服务提供商
 */
export type VoiceProvider = 'cosyvoice' | 'volcano' | 'elevenlabs'

/**
 * 音色场景
 */
export type VoiceScene = 'normal' | 'gentle' | 'angry' | 'sad' | 'singing' | 'excited' | 'professional'

/**
 * 克隆音色信息
 * v10.1 更新：新增 published 状态和合成参数字段
 */
export interface Voice {
  id: string
  name: string
  scene: VoiceScene
  is_default: boolean
  provider?: VoiceProvider
  model_id?: string
  status: VoiceStatus
  quality_score?: number
  sample_count?: number
  total_duration?: number
  // v10.1 新增：合成参数
  speech_rate?: number      // 语速 (0.5 ~ 2.0)
  pitch_rate?: number       // 音调 (0.5 ~ 2.0)
  volume?: number           // 音量 (0 ~ 100)
  instruction?: string      // 🆕 v10.4 情感指令
  published_at?: string     // 发布时间
  created_at: string
}

// ==================== 核心数据模型 ====================

/**
 * 身份信息（列表项和详情共用）
 */
export interface Identity {
  id: string
  name: string
  description?: string
  avatar?: string
  status: IdentityStatus
  version: number
  corpus_count: number
  voice_status: VoiceStatus
  voice_model_id?: string
  voice_quality_score?: number
  learning_progress?: number | null
  estimated_remaining_time?: string | null
  error_message?: string | null
  created_at: string
  updated_at: string
}

/**
 * 语料项
 * v9.2 更新：新增 name 和 scenes 字段
 */
export interface Corpus {
  id: string
  identity_id: string
  name: string                    // 🆕 v9.2 用户自定义名称
  type: CorpusType
  content: string
  target_person?: string          // 被学习人名称
  target_person_aliases?: string[] // 被学习人别名
  scenes?: SceneTag[]             // 🆕 v9.2 已关联的场景列表
  item_count?: number             // 内容条数
  metadata?: {
    source?: string
    date?: string
    [key: string]: unknown
  }
  created_at: string
  updated_at?: string
}

/**
 * 语料统计
 */
export interface CorpusSummary {
  total_count: number
  by_type: Record<CorpusType, number>
}

/**
 * 学习状态
 */
export interface LearningStatus {
  job_id: string
  status: 'running' | 'completed' | 'failed' | 'cancelled'
  progress: number
  current_stage: LearningStage
  stage_description: string
  estimated_remaining_time?: string
  started_at: string
  error_message?: string
}

// ==================== 人格配置 ====================

/**
 * 核心人格
 */
export interface PersonaCore {
  traits: string[]
  voice: {
    self: string        // 第一人称
    fan_call: string    // 称呼粉丝（单数）
    group_call: string  // 称呼粉丝（复数）
  }
  values: string[]
}

/**
 * 风格配置
 */
export interface StyleProfile {
  avg_sentence_length: number
  max_sentence_length: number
  emoji_frequency: number
  favorite_emojis: string[]
  preferred_words: string[]
  avoided_words: string[]
  fillers: string[]
}

/**
 * 情绪模式
 */
export interface EmotionPattern {
  trigger: string
  primary_response: string
  secondary_response: string
  forbidden_response: string
  example: string
}

/**
 * 边界规则
 */
export interface BoundaryGuard {
  never_say: string[]
  relationship: {
    max_intimacy_level: number
    forbidden_topics: string[]
  }
  commitment: {
    allowed: string[]
    forbidden: string[]
  }
  reality: {
    must_maintain: string
    forbidden: string[]
  }
}

/**
 * 完整人格配置
 */
export interface IdentityProfile {
  identity_id: string
  persona_core: PersonaCore
  style_profile: StyleProfile
  emotion_patterns: EmotionPattern[]
  boundary_guard: BoundaryGuard
  inject_text: string
  version: number
  learned_at: string
}

// ==================== 声音克隆 ====================

/**
 * 音频样本
 */
export interface VoiceSample {
  id: string
  file_name: string
  file_url: string
  duration: number
  status: VoiceSampleStatus
  quality_score?: number | null
  failed_reason?: string | null
  created_at: string
}

/**
 * 音频样本统计
 */
export interface VoiceSampleSummary {
  total_count: number
  total_duration: number
  passed_count: number
  failed_count: number
}

/**
 * 声音训练状态
 */
export interface VoiceTrainingStatus {
  status: VoiceStatus
  model_id?: string
  provider?: VoiceProvider
  quality_score?: number
  sample_count?: number
  total_duration?: number
  training_progress?: number | null
  estimated_remaining_time?: string | null
  created_at?: string
}

// ==================== 版本管理 ====================

/**
 * Facet 版本快照（用于 Identity 版本历史）
 */
export interface FacetVersionSnapshot {
  scene_tag: string
  scene_name: string
  version: string | null
  status: string
}

/**
 * 版本信息
 */
export interface IdentityVersion {
  version: number
  description: string
  is_current: boolean
  profile_snapshot?: IdentityProfile
  /** v9.2.2: 包含的 Facet 版本快照 */
  facet_versions?: FacetVersionSnapshot[]
  created_at: string
}

/**
 * 版本变更项
 */
export interface VersionChange {
  path: string
  type: 'added' | 'removed' | 'modified'
  value?: unknown
  old_value?: unknown
}

/**
 * 版本对比结果
 */
export interface VersionCompareResult {
  from_version: number
  to_version: number
  changes: VersionChange[]
}

// ==================== 请求/响应类型 ====================

/**
 * 身份列表查询参数
 */
export interface IdentityListParams {
  status?: IdentityStatus
  name?: string
  created_after?: string
  created_before?: string
  page?: number
  page_size?: number
}

/**
 * 分页信息
 */
export interface PaginationInfo {
  page: number
  page_size: number
  total: number
  total_pages: number
  has_next: boolean
  has_prev: boolean
}

/**
 * 身份列表响应
 */
export interface IdentityListResponse {
  data: Identity[]
  pagination: PaginationInfo
}

/**
 * 创建身份请求
 */
export interface CreateIdentityRequest {
  name: string
  description?: string
  avatar?: string
}

/**
 * 更新身份请求
 */
export interface UpdateIdentityRequest {
  name?: string
  description?: string
  avatar?: string
}

/**
 * 语料列表查询参数
 */
export interface CorpusListParams {
  type?: CorpusType
  search?: string
  page?: number
  page_size?: number
}

/**
 * 语料列表响应
 */
export interface CorpusListResponse {
  data: Corpus[]
  pagination: PaginationInfo
  summary: CorpusSummary
}

/**
 * 上传语料请求
 * v9.2 更新：新增 name 字段（必填），移除 scene_tag 字段
 * 参考：API 文档 4.2 节
 */
export interface UploadCorpusRequest {
  name: string                         // 🆕 v9.2 用户自定义名称（必填）
  target_person: string                // 被学习人名称（必填）
  target_person_aliases?: string[]     // 被学习人别名（可选）
  // 注意：v9.2 移除了 scene_tag 字段，场景关联在单独接口中进行
  corpus: Array<{
    type: CorpusType
    content: string
    metadata?: Record<string, unknown>
  }>
}

/**
 * 场景标签类型
 * 参考：API 文档 4.2 节 场景标签枚举
 */
export type SceneTag = 
  | 'work_discussion'
  | 'relationship'
  | 'family'
  | 'friends'
  | 'public'
  | 'conflict'
  | 'stress'
  | 'general'

/**
 * 上传语料响应
 */
export interface UploadCorpusResponse {
  uploaded_count: number
  total_count: number
}

/**
 * 批量导入语料响应
 */
export interface ImportCorpusResponse {
  imported_count: number
  failed_count: number
  failed_rows?: number[]
  failed_reasons?: string[]
  total_count: number
}

/**
 * 开始学习请求
 */
export interface StartLearningRequest {
  force?: boolean
}

/**
 * 更新人格配置请求（部分更新）
 */
export interface UpdateProfileRequest {
  persona_core?: Partial<PersonaCore>
  style_profile?: Partial<StyleProfile>
  emotion_patterns?: EmotionPattern[]
}

/**
 * 更新边界规则请求
 */
export interface UpdateBoundaryRequest {
  never_say?: string[]
  relationship?: Partial<BoundaryGuard['relationship']>
  commitment?: Partial<BoundaryGuard['commitment']>
  reality?: Partial<BoundaryGuard['reality']>
}

/**
 * 获取上传凭证请求
 */
export interface GetUploadUrlRequest {
  file_name: string
  content_type: string
}

/**
 * 上传凭证响应
 */
export interface UploadUrlResponse {
  upload_url: string
  file_key: string
  expires_in: number
}

/**
 * 确认音频上传请求
 */
export interface ConfirmVoiceSamplesRequest {
  samples: Array<{
    file_key: string
    file_name: string
    duration: number
  }>
}

/**
 * 确认音频上传响应
 */
export interface ConfirmVoiceSamplesResponse {
  uploaded_count: number
  processing_count: number
  message: string
}

/**
 * 开始声音训练请求
 */
export interface StartVoiceTrainingRequest {
  provider: VoiceProvider
  base_model?: string
}

/**
 * 开始声音训练响应
 */
export interface StartVoiceTrainingResponse {
  job_id: string
  status: string
  estimated_time: number
  started_at: string
}

/**
 * 声音试听请求
 * v10.2 更新：新增 model_id 参数用于指定训练模型
 */
export interface VoicePreviewRequest {
  text: string
  model_id?: string      // 🆕 v10.2 指定训练模型 ID
  emotion?: string
  format?: string        // 音频格式，默认 mp3
  speech_rate?: number   // 语速 (0.5~2.0)
  pitch_rate?: number    // 音调 (0.5~2.0)
  volume?: number        // 音量 (0~100)
}

/**
 * 声音试听响应
 */
export interface VoicePreviewResponse {
  audio_url: string
  duration: number
  expires_in: number
}

/**
 * 发布版本请求
 */
export interface PublishVersionRequest {
  description: string
}

/**
 * 头像上传确认请求
 */
export interface ConfirmAvatarRequest {
  file_key: string
}

/**
 * 创建音色请求
 */
export interface CreateVoiceRequest {
  name: string
  scene: VoiceScene
  is_default?: boolean
}

// ==================== v10.1 声音发布相关 ====================

/**
 * 发布音色请求
 * PUT /api/v1/identity/{identity_id}/voices/{voice_id}/publish
 */
export interface PublishVoiceRequest {
  speech_rate?: number   // 语速 (0.5 ~ 2.0)，默认 1.0
  pitch_rate?: number    // 音调 (0.5 ~ 2.0)，默认 1.0
  volume?: number        // 音量 (0 ~ 100)，默认 50
  instruction?: string   // 🆕 v10.4 情感指令（可选）
}

/**
 * 发布音色响应
 */
export interface PublishVoiceResponse {
  voice_id: string
  status: 'published'
  speech_rate: number
  pitch_rate: number
  volume: number
  instruction?: string   // 🆕 v10.4 情感指令
  published_at: string
}

/**
 * 取消发布响应
 */
export interface UnpublishVoiceResponse {
  voice_id: string
  status: 'ready'
  unpublished_at: string
}

/**
 * 取消发布错误响应（音色已被绑定）
 */
export interface UnpublishVoiceErrorResponse {
  error: 'VOICE_IN_USE'
  message: string
  bound_facets: string[]
}

/**
 * 更新合成配置请求
 * PATCH /api/v1/identity/{identity_id}/voices/{voice_id}/config
 */
export interface UpdateVoiceConfigRequest {
  speech_rate?: number
  pitch_rate?: number
  volume?: number
  instruction?: string   // 🆕 v10.4 情感指令（空字符串表示清除）
}

/**
 * 更新合成配置响应
 */
export interface UpdateVoiceConfigResponse {
  voice_id: string
  speech_rate: number
  pitch_rate: number
  volume: number
  instruction?: string   // 🆕 v10.4 情感指令
  updated_at: string
}

// ==================== v10.2 训练模型历史管理 ====================

/**
 * 训练模型（单个训练结果）
 * 参考：API 文档 6.15 节
 */
export interface VoiceTrainingModel {
  model_id: string           // CosyVoice 平台返回的克隆模型 ID
  name: string               // 模型名称（默认 MM-DD HH:mm）
  target_model: string       // 训练使用的基础模型
  trained_at: string         // 训练时间
  quality_score?: number     // 质量评分
  is_active: boolean         // 是否为当前使用的模型
  cosyvoice_status?: 'OK' | 'DEPLOYING' | 'UNDEPLOYED'  // CosyVoice 状态（v10.2）
}

/**
 * CosyVoice 同步状态
 * 参考：API 文档 6.19 节 SSE 事件
 */
export interface VoiceSyncStatus {
  model_id: string
  status: 'OK' | 'DEPLOYING' | 'UNDEPLOYED'
  in_system: boolean          // 是否在系统中有对应记录
  voice_card_id: string | null
  voice_card_name: string | null
}

/**
 * 孤儿音色（CosyVoice 上存在但系统无记录）
 */
export interface OrphanedVoice {
  model_id: string
  status: 'OK' | 'DEPLOYING' | 'UNDEPLOYED'
}

/**
 * 同步完成事件数据
 */
export interface VoiceSyncCompleteData {
  total: number
  in_system: number
  orphaned: number
}

/**
 * 获取训练模型历史响应
 * 参考：API 文档 6.15 节
 */
export interface ListVoiceModelsResponse {
  items: VoiceTrainingModel[]
  total: number
  active_model_id: string | null
}

/**
 * 激活训练模型响应
 * 参考：API 文档 6.16 节
 */
export interface ActivateVoiceModelResponse {
  model_id: string
  is_active: true
  activated_at: string
}

/**
 * 删除训练模型响应
 * 参考：API 文档 6.17 节
 */
export interface DeleteVoiceModelResponse {
  model_id: string
  deleted: boolean
  cosyvoice_deleted: boolean
  message: string
}

/**
 * 重命名训练模型请求
 * 参考：API 文档 6.18 节
 */
export interface RenameVoiceModelRequest {
  name: string
}

/**
 * 重命名训练模型响应
 */
export interface RenameVoiceModelResponse {
  model_id: string
  name: string
  updated_at: string
}

// ==================== 常量配置 ====================

/**
 * 身份状态显示配置
 */
export const IDENTITY_STATUS_CONFIG: Record<IdentityStatus, { 
  label: string
  color: string
  bgColor: string
  icon: string
}> = {
  draft: { label: '草稿', color: 'text-slate-600', bgColor: 'bg-slate-100', icon: '📝' },
  learning: { label: '学习中', color: 'text-violet-600', bgColor: 'bg-violet-100', icon: '🧠' },
  active: { label: '已激活', color: 'text-emerald-600', bgColor: 'bg-emerald-100', icon: '✅' },
  archived: { label: '已归档', color: 'text-gray-500', bgColor: 'bg-gray-100', icon: '📦' },
  failed: { label: '失败', color: 'text-red-600', bgColor: 'bg-red-100', icon: '❌' },
}

/**
 * 声音状态显示配置
 * v10.1 更新：新增 pending 和 published 状态
 */
export const VOICE_STATUS_CONFIG: Record<VoiceStatus, {
  label: string
  color: string
  icon: string
}> = {
  none: { label: '未克隆', color: 'text-gray-400', icon: '🔇' },
  pending: { label: '待训练', color: 'text-slate-500', icon: '⏸️' },
  training: { label: '训练中', color: 'text-amber-500', icon: '⏳' },
  ready: { label: '已就绪', color: 'text-blue-500', icon: '🎙️' },
  published: { label: '已发布', color: 'text-emerald-500', icon: '✅' },
  failed: { label: '训练失败', color: 'text-red-500', icon: '❌' },
}

/**
 * 语料类型显示配置
 */
export const CORPUS_TYPE_CONFIG: Record<CorpusType, {
  label: string
  icon: string
}> = {
  weibo: { label: '微博', icon: '📱' },
  interview: { label: '采访', icon: '🎤' },
  live_chat: { label: '直播', icon: '📺' },
  dialog: { label: '对话', icon: '💬' },
  other: { label: '其他', icon: '📄' },
}

/**
 * 学习阶段显示配置
 */
export const LEARNING_STAGE_CONFIG: Record<LearningStage, {
  label: string
  description: string
  progressRange: [number, number]
}> = {
  preparing: { label: '准备中', description: '📋 准备中，加载语料...', progressRange: [0, 5] },
  style: { label: '风格分析', description: '📝 正在分析表达风格和用词习惯...', progressRange: [5, 30] },
  persona: { label: '人格提取', description: '🎭 正在提取核心人格特质...', progressRange: [30, 60] },
  emotion: { label: '情绪识别', description: '💕 正在识别情绪反应模式...', progressRange: [60, 85] },
  boundary: { label: '边界生成', description: '🛡️ 正在生成边界规则...', progressRange: [85, 95] },
  finalizing: { label: '完成中', description: '✅ 正在生成最终配置...', progressRange: [95, 100] },
}

/**
 * TTS 服务提供商配置
 */
export const VOICE_PROVIDER_CONFIG: Record<VoiceProvider, {
  label: string
  description: string
}> = {
  cosyvoice: { label: 'CosyVoice', description: '阿里通义实验室' },
  volcano: { label: '火山引擎', description: '字节跳动' },
  elevenlabs: { label: 'ElevenLabs', description: 'AI语音合成平台' },
}

/**
 * 音色场景配置
 */
export const VOICE_SCENE_CONFIG: Record<VoiceScene, {
  label: string
  icon: string
  description: string
}> = {
  normal: { label: '正常', icon: '🎤', description: '日常说话' },
  gentle: { label: '温柔', icon: '💕', description: '温柔撒娇' },
  angry: { label: '生气', icon: '😤', description: '生气训斥' },
  sad: { label: '难过', icon: '😢', description: '悲伤低落' },
  singing: { label: '唱歌', icon: '🎵', description: '歌唱模式' },
  excited: { label: '兴奋', icon: '🎉', description: '激动兴奋' },
  professional: { label: '专业', icon: '💼', description: '正式专业' },
}

// ==================== 辅助函数 ====================

/**
 * 获取身份状态标签
 */
export function getIdentityStatusLabel(status: IdentityStatus): string {
  return IDENTITY_STATUS_CONFIG[status]?.label || status
}

/**
 * 获取声音状态标签
 */
export function getVoiceStatusLabel(status: VoiceStatus): string {
  return VOICE_STATUS_CONFIG[status]?.label || status
}

/**
 * 获取语料类型标签
 */
export function getCorpusTypeLabel(type: CorpusType): string {
  return CORPUS_TYPE_CONFIG[type]?.label || type
}

/**
 * 获取学习阶段描述
 */
export function getLearningStageDescription(stage: LearningStage): string {
  return LEARNING_STAGE_CONFIG[stage]?.description || stage
}

/**
 * 格式化音频时长（秒 -> 分:秒）
 */
export function formatDuration(seconds: number): string {
  const mins = Math.floor(seconds / 60)
  const secs = Math.floor(seconds % 60)
  return `${mins}:${secs.toString().padStart(2, '0')}`
}

/**
 * 格式化总时长（秒 -> 小时分钟）
 */
export function formatTotalDuration(seconds: number): string {
  if (seconds < 60) {
    return `${Math.floor(seconds)} 秒`
  }
  if (seconds < 3600) {
    return `${Math.floor(seconds / 60)} 分钟`
  }
  const hours = Math.floor(seconds / 3600)
  const mins = Math.floor((seconds % 3600) / 60)
  return mins > 0 ? `${hours} 小时 ${mins} 分钟` : `${hours} 小时`
}

// ==================== v10.3 训练进度 SSE ====================

/**
 * 训练阶段
 * 参考：API 文档 6.21 节
 */
export type TrainingPhase = 'preparing' | 'audio_processing' | 'model_training' | 'completing'

/**
 * 训练进度事件数据
 * 参考：API 文档 6.21 节 progress 事件
 */
export interface TrainingProgressData {
  phase: TrainingPhase
  current?: number
  total?: number
  percent: number
  message: string
}

/**
 * 训练完成事件数据
 * 参考：API 文档 6.21 节 complete 事件
 */
export interface TrainingCompleteData {
  status: 'ready'
  model_id: string
  cosyvoice_status: 'OK' | 'DEPLOYING'
  message: string
}

/**
 * 训练错误事件数据
 * 参考：API 文档 6.21 节 error 事件
 */
export interface TrainingErrorData {
  code: string
  message: string
}

/**
 * 训练阶段显示配置
 */
export const TRAINING_PHASE_CONFIG: Record<TrainingPhase, {
  label: string
  icon: string
}> = {
  preparing: { label: '准备中', icon: '📋' },
  audio_processing: { label: '音频处理', icon: '🎵' },
  model_training: { label: '模型训练', icon: '🧠' },
  completing: { label: '完成中', icon: '✅' },
}

// ==================== v1.7 系统配置 ====================

/**
 * 声音训练模型选项
 */
export interface VoiceTrainingModelOption {
  model_id: string
  name: string
  description?: string
  is_default: boolean
  features?: string[]
}

/**
 * 声音训练模型（按提供商分组）
 */
export interface VoiceTrainingModels {
  cosyvoice: VoiceTrainingModelOption[]
}

/**
 * 学习 LLM 模型选项
 */
export interface LearningLLMModelOption {
  model_id: string
  name: string
  provider: string
  description?: string
  is_default: boolean
}

/**
 * 系统配置（完整响应）
 */
export interface SystemConfig {
  voice_training_models: VoiceTrainingModels
  learning_llm_models: LearningLLMModelOption[]
}

/**
 * Facet 学习请求（v1.7 增强，支持模型选择）
 */
export interface StartFacetLearningRequest {
  force?: boolean
  model?: string  // LLM 模型 ID
}

// ==================== v10.4 情感指令 ====================

/**
 * 单个情感指令选项
 */
export interface VoiceInstruction {
  id: string           // 指令 ID，如 "happy", "cantonese"
  instruction: string  // 完整指令文本，如 "请非常开心地说一句话。"
  name: string         // 友好名称，如 "开心", "广东话"
}

/**
 * 情感指令分类
 */
export interface VoiceInstructionCategory {
  emotion: VoiceInstruction[]   // 情绪类指令
  dialect: VoiceInstruction[]   // 方言类指令
  model_support: Record<string, string[]>  // 模型支持的指令类型
}

/**
 * 情感指令配置响应
 * GET /api/v1/config/voice-instructions
 */
export interface VoiceInstructionsConfig {
  cosyvoice: VoiceInstructionCategory
}

/**
 * 根据模型获取可用的指令列表
 * @param config 指令配置
 * @param targetModel 训练模型 ID（如 "cosyvoice-v3-flash"）
 * @returns 可用的指令列表（合并 emotion 和 dialect）
 */
export function getAvailableInstructions(
  config: VoiceInstructionsConfig | null,
  targetModel: string
): VoiceInstruction[] {
  if (!config?.cosyvoice) return []
  
  const { emotion, dialect, model_support } = config.cosyvoice
  const supportedTypes = model_support[targetModel] || []
  
  const result: VoiceInstruction[] = []
  
  if (supportedTypes.includes('emotion')) {
    result.push(...emotion)
  }
  if (supportedTypes.includes('dialect')) {
    result.push(...dialect)
  }
  
  return result
}

/**
 * 根据 instruction 文本查找对应的友好名称
 */
export function getInstructionName(
  config: VoiceInstructionsConfig | null,
  instruction: string | null | undefined
): string | null {
  if (!config?.cosyvoice || !instruction) return null
  
  const { emotion, dialect } = config.cosyvoice
  const allInstructions = [...emotion, ...dialect]
  
  const found = allInstructions.find(i => i.instruction === instruction)
  return found?.name || null
}
